/**
 * LLM-Based Anchor Selection Service
 * 
 * Optimized approach: Send clean code to LLM for intelligent curation
 * LLM selects the most valuable learning opportunities for the user's background
 * Much more efficient than sending 30+ Tree-sitter anchor objects with metadata
 */

import { LensAnchorDescriptor, AnchorSelection } from '../types/lensAnchorTypes';

interface LLMSelection {
  text: string;
  reason: string;
  novelty: number;
  diffType?: string;
}

interface LLMSelectionRequest {
  code: string;
  known: string;
  target: string;
  max: number;
  provider?: {
    provider: string;
    model: string;
    status?: string;
  };
}

interface LLMSelectionResponse {
  selections: LLMSelection[];
  metadata: {
    responseTime: number;
    tokensSaved: number;
    prompt?: string;
    tokensUsed?: {
      input: number;
      output: number;
      total: number;
    };
    apiEndpoint?: string;
    timestamp?: string;
    model?: string;
    requestId?: string;
    optimization?: string;
    traditionalTokens?: number;
    actualTokens?: number;
    percentageSaved?: number;
  };
}

/**
 * Send clean code snippet to LLM for intelligent anchor selection
 * LLM analyzes code and selects most valuable learning opportunities
 * Much more efficient than sending 30+ Tree-sitter anchor objects with metadata
 */
import { getAPIHeaders } from '../utils/apiHeaders';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export async function selectAnchorsFromLLM(request: LLMSelectionRequest): Promise<LLMSelectionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/lens-anchors/suggest-from-code`, {
    method: 'POST',
    headers: getAPIHeaders(),
    body: JSON.stringify(request)
  });
  
  if (!response.ok) {
    const errorResponse = await response.json();
    console.error('🚨 LLM API Error:', response.status, response.statusText, errorResponse);
    
    // Even error responses might contain prompts for observability
    if (errorResponse.metadata?.prompt) {
      console.log('🔍 Error response contains prompt for observability:', {
        hasPrompt: !!errorResponse.metadata.prompt,
        promptLength: errorResponse.metadata.prompt.length
      });
    }
    
    throw new Error(`LLM anchor selection failed: ${response.statusText} - ${errorResponse.error || 'Unknown error'}`);
  }
  
  const jsonResponse = await response.json();
  
  // Debug logging for prompt capture
  console.log('🔍 LLM API Response Debug:', {
    hasMetadata: !!jsonResponse.metadata,
    hasPrompt: !!jsonResponse.metadata?.prompt,
    promptLength: jsonResponse.metadata?.prompt?.length,
    promptPreview: jsonResponse.metadata?.prompt?.substring(0, 100),
    metadataKeys: jsonResponse.metadata ? Object.keys(jsonResponse.metadata) : []
  });
  
  return jsonResponse;
}

/**
 * Match LLM text selections to precise AST positions
 * This gives us the best of both worlds:
 * - LLM sees clean, readable code
 * - We get precise positioning from Tree-sitter
 */
export function matchSelectionsToAST(
  llmSelections: LLMSelection[],
  astAnchors: LensAnchorDescriptor[]
): AnchorSelection[] {
  
  return llmSelections.map(selection => {
    // Try exact text match first
    let match = astAnchors.find(anchor => anchor.text === selection.text);
    
    // Try partial match (LLM might select part of a token)
    if (!match) {
      match = astAnchors.find(anchor => 
        anchor.text.includes(selection.text) ||
        selection.text.includes(anchor.text)
      );
    }
    
    // Try fuzzy match for slight variations
    if (!match) {
      match = astAnchors.find(anchor => 
        fuzzyMatch(anchor.text.toLowerCase(), selection.text.toLowerCase(), 0.8)
      );
    }
    
    if (!match) {
      console.warn(`No AST match found for LLM selection: "${selection.text}"`);
      // Create synthetic anchor for unmatched selections
      return createSyntheticAnchor(selection);
    }
    
    return {
      tempId: match.tempId,
      novelty: selection.novelty,
      diffType: selection.diffType ? [selection.diffType] : inferDiffType(selection.reason),
      why: selection.reason,
      granularity: match.granularity,
      group: match.features[0]?.split(':')[0] || 'unknown',
      priority: llmSelections.indexOf(selection) + 1,
      
      // Precise positioning from Tree-sitter
      span: match.span,
      surrounding: match.surrounding,
      nodeType: match.nodeType,
      features: match.features
    };
  });
}

/**
 * Fuzzy string matching for slight variations in text
 */
function fuzzyMatch(str1: string, str2: string, threshold: number = 0.8): boolean {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return true;
  
  const similarity = (longer.length - editDistance(longer, shorter)) / longer.length;
  return similarity >= threshold;
}

/**
 * Calculate edit distance between two strings
 */
function editDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const substitutionCost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + substitutionCost // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Infer diff types from LLM reasoning text
 */
function inferDiffType(reason: string): string[] {
  const lower = reason.toLowerCase();
  const types: string[] = [];
  
  if (lower.includes('paradigm') || lower.includes('ownership') || lower.includes('memory safety')) {
    types.push('paradigm');
  }
  
  if (lower.includes('syntax') || lower.includes('operator') || lower.includes('keyword')) {
    types.push('syntax');
  }
  
  if (lower.includes('semantic') || lower.includes('meaning') || lower.includes('behavior')) {
    types.push('semantic');
  }
  
  if (lower.includes('idiom') || lower.includes('pattern') || lower.includes('convention')) {
    types.push('idiom');
  }
  
  return types.length > 0 ? types : ['syntax'];
}

/**
 * Create synthetic anchor for LLM selections that don't match AST
 */
function createSyntheticAnchor(selection: LLMSelection): AnchorSelection {
  return {
    tempId: `synthetic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    novelty: selection.novelty,
    diffType: selection.diffType ? [selection.diffType] : inferDiffType(selection.reason),
    why: selection.reason,
    granularity: 'token',
    group: 'llm_generated',
    priority: 1
  };
}

/**
 * Calculate token savings compared to sending full AST metadata
 */
export function calculateTokenSavings(codeLength: number, anchorCount: number): number {
  const codeTokens = Math.ceil(codeLength / 4); // Rough estimate
  const astTokens = anchorCount * 20; // Rough estimate per anchor with metadata
  
  return Math.max(0, astTokens - codeTokens);
}